/**
 * Deferred Optimizely bootstrap plugin.
 *
 * Purpose:
 * - Inject Optimizely SDK on client without blocking first paint.
 *
 * Why deferred:
 * - Experiments are important, but loading them immediately can compete with
 *   hero rendering and critical UI work. Delayed injection improves perceived speed.
 *
 * How it works:
 * - Client-only execution.
 * - Guards duplicate script insertion by id.
 * - Schedules injection after load and preferably during browser idle time.
 *
 * Future dev notes:
 * - Keep async/defer + idle scheduling unless product explicitly requires
 *   earliest possible experiment activation over performance.
 */

export default defineNuxtPlugin(() => {
  if (process.client) {
    const injectScript = () => {
      // Idempotent guard for SPA navigations/hot reloads.
      if (document.getElementById('optimizely-sdk-script')) {
        return;
      }

      const script = document.createElement('script');
      script.id = 'optimizely-sdk-script';
      script.src = 'https://cdn.optimizely.com/js/19075480916.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    const scheduleInjection = () => {
      // Idle callback keeps experimentation setup off the critical render path.
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(injectScript, { timeout: 4000 });
        return;
      }

      // Fallback for browsers without requestIdleCallback support.
      setTimeout(injectScript, 2500);
    };

    if (document.readyState === 'complete') {
      scheduleInjection();
      return;
    }

    window.addEventListener('load', scheduleInjection, { once: true });
  }
});
